/**
 * Profile settings — account-level preferences.
 *
 * Currently one setting: which app K-OS opens after you sign in. Saves on
 * change through the shared preferences endpoint (`scope: 'platform'`), the
 * same row that owns theme and density — matching how Appearance behaves, so
 * there is no save button.
 *
 * Note this fires at SIGN-IN only. Opening K-OS with a session cookie already
 * live still lands on the picker; see the design spec [[profile-landing-app]]
 * §0, where that limitation is deliberate.
 */

import { LANDING_APPS, PLATFORM_APPS, isLandingApp, type LandingApp } from '@k-os/core';
import { useSession } from '../../api/auth-hooks';
import { ApiError } from '../../api/client';
import { usePatchPreference, usePlatformPref } from '../../api/preferences';
import { Segmented } from '../../components/Segmented';
import styles from './Approvals.module.css';

const LANDING_OPTIONS: readonly { value: LandingApp; label: string }[] = LANDING_APPS.map(
  (value) => ({
    value,
    label: value === 'platform' ? 'Platform' : PLATFORM_APPS[value].name,
  }),
);

/**
 * Turn a failed PATCH into something actionable rather than "an error
 * occurred". The status is the useful part: a 500 here almost always means
 * the `landing_app` migration hasn't been applied to this environment.
 */
function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 500) {
      return 'The server rejected it (500) — this environment may be missing the landing_app migration.';
    }
    if (error.status === 401) return 'Your session expired — sign in again.';
    return `The server returned ${error.status}.`;
  }
  return 'Check your connection and try again.';
}

export function ProfileSettingsPage() {
  const session = useSession();
  const user = session.data?.user;
  const platformPref = usePlatformPref();
  const patch = usePatchPreference();

  const stored = platformPref?.landingApp;
  const landing: LandingApp = stored && isLandingApp(stored) ? stored : 'platform';

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Profile settings</h1>
        <p className={styles.sub}>
          Account-level preferences. Display name + email come from your
          signup — full editing arrives in a later block.
        </p>
      </header>

      {user && (
        <div className={styles.empty}>
          Signed in as <strong>{user.displayName}</strong> · {user.email}
        </div>
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Default app</h2>
        <p className={styles.sectionSub}>
          Which app opens when you sign in. Platform shows the app picker.
        </p>
        <Segmented
          ariaLabel="Default app on sign-in"
          value={landing}
          options={LANDING_OPTIONS}
          onChange={(value) => patch.mutate({ scope: 'platform', landingApp: value })}
        />
        {/* Without this the control is indistinguishable from a dead one: the
         * only feedback a save-on-change segmented control has is the
         * selection moving, so a rejected PATCH looks exactly like a click
         * that did nothing. */}
        {patch.isError && (
          <p className={styles.sectionError} role="alert">
            Couldn’t save that. {describeError(patch.error)}
          </p>
        )}
      </section>
    </div>
  );
}
